import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import ReputationPill from '../common/ReputationPill';

interface IdeaMetricsChipsProps {
  pulseData: any;
  draft: any;
  isMobile: boolean;
  isVerySmall: boolean;
  impactColor: string;
  title: string;
  showSimulationButton?: boolean;
  simulationButton?: React.ReactNode;
}

const IdeaMetricsChips: React.FC<IdeaMetricsChipsProps> = ({
  pulseData,
  draft,
  isMobile,
  isVerySmall,
  impactColor,
  title,
  showSimulationButton = false,
  simulationButton
}) => {
  // Mobile: KPI Pills Inside Card (≤360px)
  if (isVerySmall) {
    return (
      <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ 
        fontWeight: 600, 
        color: 'text.primary',
        fontSize: '15px',
        mb: 2
      }}>
        {title}
      </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                    {pulseData.positivePct || draft.positivePct || 0}% Positive
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#06D6A0', 
                color: 'white',
                width: '100%',
                fontSize: '11px',
                fontWeight: 700
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                    {pulseData.negativePct || draft.negativePct || 0}% Negative
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#FF6B6B', 
                color: 'white',
                width: '100%',
                fontSize: '11px',
                fontWeight: 700
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                    {pulseData.replies7d || draft.replies7d || 0} Replies
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#6B7A8D', 
                color: 'white',
                width: '100%',
                fontSize: '11px',
                fontWeight: 700
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Chip 
              label={`${Math.round(pulseData.reputation?.uiScore || draft.reputation?.uiScore || pulseData.score || draft.score || draft.projectedImpactScore || 0)} • ${(pulseData.reputation?.level || draft.reputation?.level || draft.projectedImpactLevel || 'Pending').split(' ')[0]}`}
              sx={{ 
                backgroundColor: impactColor + '20',
                color: impactColor,
                fontWeight: 'bold',
                width: '100%',
                fontSize: '11px'
              }}
            />
          </Grid>
        </Grid>
        {simulationButton && (
          <Box sx={{ mt: 2 }}>
            {simulationButton}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Typography variant="h6" sx={{ 
        fontWeight: 600, 
        color: 'text.primary',
        fontSize: isMobile ? '15px' : '1.25rem',
        mb: 2
      }}>
        {title}
      </Typography>
      
      {/* Mobile: 2x2 Grid underneath header */}
      {isMobile ? (
        <>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                      {pulseData.positivePct || draft.positivePct || 0}% Positive
                    </Typography>
                  </Box>
                }
                sx={{ 
                  bgcolor: '#06D6A0', 
                  color: 'white',
                  width: '100%',
                  fontSize: '11px',
                  fontWeight: 700
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                      {pulseData.negativePct || draft.negativePct || 0}% Negative
                    </Typography>
                  </Box>
                }
                sx={{ 
                  bgcolor: '#FF6B6B', 
                  color: 'white',
                  width: '100%',
                  fontSize: '11px',
                  fontWeight: 700
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                      {pulseData.replies7d || draft.replies7d || 0} Replies
                    </Typography>
                  </Box>
                }
                sx={{ 
                  bgcolor: '#6B7A8D', 
                  color: 'white',
                  width: '100%',
                  fontSize: '11px',
                  fontWeight: 700
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                      {((pulseData.likes7d || draft.likes7d || 0) / 1000).toFixed(1)}k Likes
                    </Typography>
                  </Box>
                }
                sx={{ 
                  bgcolor: '#6B7A8D', 
                  color: 'white',
                  width: '100%',
                  fontSize: '11px',
                  fontWeight: 700
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Chip 
                label={`${Math.round(pulseData.reputation?.uiScore || draft.reputation?.uiScore || pulseData.score || draft.score || draft.projectedImpactScore || 0)} • ${(pulseData.reputation?.level || draft.reputation?.level || draft.projectedImpactLevel || 'Pending').split(' ')[0]}`}
                sx={{ 
                  backgroundColor: impactColor + '20',
                  color: impactColor,
                  fontWeight: 'bold',
                  width: '100%',
                  fontSize: '11px'
                }}
              />
            </Grid>
          </Grid>
          {simulationButton && (
            <Box sx={{ mt: 2 }}>
              {simulationButton}
            </Box>
          )}
        </>
      ) : (
        /* Desktop: Right-floating pills */
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -4, mb: 2 }}>
          <Stack 
            direction="row"
            spacing={1} 
            flexWrap="wrap" 
            useFlexGap
            alignItems="center"
          >
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" component="span" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '1rem' 
                  }}>
                    {pulseData.positivePct || draft.positivePct || 0}% Positive
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#06D6A0', 
                color: 'white',
                height: '32px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            />
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" component="span" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '1rem' 
                  }}>
                    {pulseData.negativePct || draft.negativePct || 0}% Negative
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#FF6B6B', 
                color: 'white',
                height: '32px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            />
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" component="span" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '1rem' 
                  }}>
                    {pulseData.replies7d || draft.replies7d || 0}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ 
                    opacity: 0.8, 
                    fontSize: '0.75rem' 
                  }}>
                    replies
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#6B7A8D', 
                color: 'white',
                height: '32px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            />
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" component="span" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '1rem' 
                  }}>
                    {((pulseData.likes7d || draft.likes7d || 0) / 1000).toFixed(1)}k
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ 
                    opacity: 0.8, 
                    fontSize: '0.75rem' 
                  }}>
                    likes
                  </Typography>
                </Box>
              }
              sx={{ 
                bgcolor: '#6B7A8D', 
                color: 'white',
                height: '32px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            />
            <ReputationPill 
              uiScore={Math.round(pulseData.reputation?.uiScore || draft.reputation?.uiScore || pulseData.score || draft.score || draft.projectedImpactScore || 0)} 
              level={pulseData.reputation?.level || draft.reputation?.level || draft.projectedImpactLevel || 'Pending Analysis'} 
              color={pulseData.reputation?.color || draft.reputation?.color}
            />

            {simulationButton}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default IdeaMetricsChips;
